import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLocalization } from '@/hooks/useLocalization';

// Registered as this app's Azure AD redirect_uri (must be a single fixed
// URL — Azure doesn't support a per-tenant-slug path). Microsoft redirects
// the browser here after the admin approves (or denies) consent; the SPA
// still has the Supabase session in localStorage, so this leg stays
// authenticated even though it isn't under /:slug/.
export default function MicrosoftConsentCallback() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'working' | 'success' | 'error'>('working');
  const [message, setMessage] = useState('Finishing Microsoft 365 connection…');
  // Resolved via auth_tenant_id() + a tenants lookup rather than
  // useAuth().tenantSlug: that value comes from session.user.app_metadata,
  // which reflects auth.users.raw_app_meta_data and is not reliably kept in
  // sync with the tenant_id/tenant_slug claims a Custom Access Token Hook
  // injects into the JWT itself — the same reason Integrations.tsx resolves
  // its tenant via this RPC instead of useAuth().tenantId.
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (authLoading || ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const tenant = params.get('tenant');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    (async () => {
      // Resolve regardless of outcome so "Back to Integrations" works on
      // the error path too, not just the success-redirect path.
      const { data: tenantId } = await supabase.rpc('auth_tenant_id');
      if (tenantId) {
        const { data: tenant } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .maybeSingle();
        setTenantSlug(tenant?.slug ?? null);
      }

      const { data, error: invokeError } = await supabase.functions.invoke('graph-consent-complete', {
        body: { tenant, state, error, error_description: errorDescription },
      });

      if (invokeError || !data?.success) {
        setStatus('error');
        setMessage(data?.error || invokeError?.message || 'Could not complete the Microsoft 365 connection.');
        return;
      }

      setStatus('success');
      setMessage('Microsoft 365 connected successfully.');
    })();
  }, [authLoading]);

  useEffect(() => {
    if (status === 'success' && tenantSlug) {
      const t = setTimeout(() => navigate(`/${tenantSlug}/integrations`, { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [status, tenantSlug, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-sm text-center space-y-4">
        {status === 'working' && <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />}
        {status === 'success' && <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />}
        {status === 'error' && <XCircle className="h-10 w-10 mx-auto text-destructive" />}
        <p className="text-sm text-muted-foreground">{message}</p>
        {status === 'error' && tenantSlug && (
          <button
            className="text-sm underline text-primary"
            onClick={() => navigate(`/${tenantSlug}/integrations`, { replace: true })}
          >
            
            {t('Back to Integrations')}
          </button>
        )}
      </div>
    </div>
  );
}
